import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { 
  Heart, 
  Calendar, 
  MapPin, 
  Music, 
  Pause, 
  ChevronDown, 
  Gift,
  Clock,
  Users,
  Utensils,
  Car,
  Hotel,
  ExternalLink,
  Send,
  Play,
  Menu,
  X,
  Eye,
  Check,
  XCircle,
  ArrowLeft,
  Trash2,
  Phone,
  UserPlus,
  Lock,
  Sparkles,
  Edit2,
  Save,
  AlertCircle,
  MessageCircle,
  Download,
  LogOut,
  Gem,
  Plane,
  BookHeart
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Switch } from "./components/ui/switch";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "ng_admin_token";

// Axios instance for admin (dashboard) calls — auto-attaches Bearer token
const adminAxios = axios.create({ baseURL: API });
adminAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
adminAxios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      window.dispatchEvent(new Event("admin-token-expired"));
    }
    return Promise.reject(err);
  }
);

// Couple's actual photos - 5 May 2026 batch + 3 May 2026 additional (8 total)
const couplePhotos = [
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/i0pc8qee_WhatsApp%20Image%202026-05-13%20at%2022.04.11.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/2u10zcye_WhatsApp%20Image%202026-05-13%20at%2022.04.32.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/wvsbn97c_WhatsApp%20Image%202026-05-13%20at%2022.15.51.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/9jmj8pyl_WhatsApp%20Image%202026-05-13%20at%2022.17.02.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/51dtensb_WhatsApp%20Image%202026-05-13%20at%2022.22.05.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/azmwcky8_WhatsApp%20Image%202026-05-13%20at%2022.24.59.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/o6z8e4zt_WhatsApp%20Image%202026-05-13%20at%2022.26.05.jpeg",
  "https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/t4hz0unu_WhatsApp%20Image%202026-05-13%20at%2022.26.32.jpeg"
];

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Navigation Component - Mobile Responsive
const Navigation = ({ rsvpSubmitted }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: "story", label: "Our Story" },
    { id: "rsvp", label: "RSVP" },
    ...(rsvpSubmitted ? [{ id: "schedule", label: "Schedule" }] : []),
    { id: "registry", label: "Registry" },
    { id: "location", label: "Location" },
    { id: "faq", label: "FAQ" },
  ];

  const handleNavClick = (id) => {
    setIsMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'nav-glass bg-off-white/95 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="#hero" className="font-serif text-xl tracking-tight italic bg-burgundy/80 px-3 py-1 rounded text-white">
            N & G
          </a>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                data-testid={`nav-${item.id}`}
                className={`text-sm tracking-wide transition-colors hover:text-burgundy ${
                  isScrolled ? 'text-gray-700' : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button — always legible (burgundy pill on dark hero, plain icon when scrolled) */}
          <button 
            className={`md:hidden p-2 rounded-full transition-colors ${
              isScrolled || isMobileMenuOpen
                ? 'text-burgundy hover:bg-burgundy/10'
                : 'text-white bg-burgundy/70 hover:bg-burgundy/90'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu — fully opaque so it never blends with the dark hero behind */}
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-off-white border-t border-burgundy/10 shadow-lg"
            data-testid="mobile-menu"
          >
            <div className="py-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="block w-full text-left px-5 py-3.5 text-gray-800 font-medium border-b border-burgundy/5 last:border-b-0 hover:bg-burgundy/5 hover:text-burgundy transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Infinite Scrolling Photo Marquee - Truly seamless
const PhotoMarquee = () => {
  // Render every photo TWICE inside a single flex track. The animation translates
  // the track by exactly -50% of its width which is exactly one full set of photos,
  // so the snap-back to 0 is visually identical and produces no flash.
  const photos = [...couplePhotos, ...couplePhotos];
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="marquee-container">
        <div className="marquee-track">
          {photos.map((photo, index) => (
            <img
              key={index}
              src={photo}
              alt={index < couplePhotos.length ? `Navina & George ${index + 1}` : ''}
              aria-hidden={index >= couplePhotos.length}
              loading="eager"
              decoding="async"
              className="h-screen w-auto object-cover flex-shrink-0 select-none"
              draggable={false}
              style={{ filter: 'grayscale(100%) contrast(1.1) brightness(0.95)' }}
            />
          ))}
        </div>
      </div>
      {/* Burgundy overlay */}
      <div className="absolute inset-0 bg-burgundy/60 pointer-events-none" />
      {/* Edge gradients */}
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-burgundy/70 to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-burgundy/70 to-transparent pointer-events-none" />
    </div>
  );
};

// Hero Section with Infinite Scrolling Photos
const HeroSection = () => {
  const scrollToRSVP = () => {
    document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <PhotoMarquee />
      
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto"
      >
        <motion.p
          variants={fadeUp}
          className="text-[10px] sm:text-xs uppercase tracking-[0.35em] sm:tracking-[0.45em] text-white/85 mb-5 sm:mb-7 font-sans"
        >
          Together with their loved ones
        </motion.p>

        <motion.h1 
          variants={fadeUp}
          className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium tracking-tight mb-4 sm:mb-5 italic leading-[1.05]"
        >
          Navina &amp; George
        </motion.h1>
        
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 sm:gap-4 mb-5 sm:mb-7">
          <span className="w-12 sm:w-20 h-px bg-white/60" />
          <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" />
          <span className="w-12 sm:w-20 h-px bg-white/60" />
        </motion.div>
        
        <motion.p 
          variants={fadeUp}
          className="text-sm sm:text-base md:text-lg font-sans tracking-[0.08em] text-white/95 leading-relaxed"
        >
          request the pleasure of your company
        </motion.p>

        <motion.p
          variants={fadeUp}
          className="text-sm sm:text-base md:text-lg font-sans tracking-[0.08em] text-white/95 leading-relaxed mb-7 sm:mb-9"
        >
          to celebrate their wedding
        </motion.p>
        
        <motion.p 
          variants={fadeUp}
          className="font-serif italic text-2xl sm:text-3xl md:text-4xl mb-5 sm:mb-6 text-white"
        >
          14th November 2026
        </motion.p>
        
        <motion.p 
          variants={fadeUp}
          className="text-xs sm:text-sm font-sans tracking-[0.15em] uppercase flex items-center justify-center gap-2 mb-8 sm:mb-10 text-white/90"
        >
          <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          voco Manchester City Centre
        </motion.p>
        
        <motion.div variants={fadeUp}>
          <Button 
            data-testid="hero-rsvp-button"
            onClick={scrollToRSVP}
            className="bg-white hover:bg-gray-100 text-burgundy px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base tracking-wider font-medium"
          >
            RSVP Now
          </Button>
        </motion.div>
        
        <motion.div 
          variants={fadeUp}
          className="mt-12 sm:mt-16"
        >
          <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8 animate-bounce mx-auto" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Hand-drawn looping illustrations for the love-story timeline
// Each is an inline SVG (burgundy ink lines, no fill) with its own continuous Framer-Motion loop.

// 2023 — clinking champagne flutes with sparkles ("the meet")
const ChampagneToastDoodle = () => (
  <motion.svg
    viewBox="0 0 100 100" fill="none" stroke="#4A1A1F" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"
    animate={{ rotate: [-4, 4, -4] }}
    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
  >
    {/* Left flute */}
    <path d="M25 18 C25 38, 30 50, 38 56 L38 78" />
    <path d="M45 18 C45 38, 41 50, 38 56" />
    <path d="M30 78 L46 78" />
    {/* Right flute, leaning toward left */}
    <path d="M55 18 C55 38, 59 50, 62 56 L62 78" />
    <path d="M75 18 C75 38, 70 50, 62 56" />
    <path d="M54 78 L70 78" />
    {/* Liquid lines */}
    <path d="M30 28 C33 32, 42 32, 44 28" opacity="0.7" />
    <path d="M56 28 C58 32, 70 32, 73 28" opacity="0.7" />
    {/* Sparkles */}
    <motion.g
      animate={{ opacity: [0.3, 1, 0.3], scale: [0.85, 1.1, 0.85] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "50px 50px" }}
    >
      <path d="M18 12 L20 16 L24 14 L20 18 L18 22 L16 18 L12 16 L16 14 Z" />
      <path d="M82 14 L83 17 L86 16 L84 19 L83 22 L82 19 L79 18 L82 16 Z" />
      <path d="M50 8 L51 11 L54 10 L52 13 L51 16 L50 13 L47 12 L50 10 Z" />
    </motion.g>
  </motion.svg>
);

// 2024 — two interlocking hearts ("growing together")
const HeartsDoodle = () => (
  <motion.svg
    viewBox="0 0 100 100" fill="none" stroke="#4A1A1F" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"
    animate={{ scale: [1, 1.08, 1] }}
    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
  >
    {/* Left heart */}
    <motion.path
      d="M40 32 C40 24, 30 22, 26 30 C22 22, 12 24, 12 32 C12 44, 26 58, 26 58 C26 58, 40 44, 40 32 Z"
      animate={{ rotate: [-3, 3, -3] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "26px 40px" }}
    />
    {/* Right heart */}
    <motion.path
      d="M88 42 C88 34, 78 32, 74 40 C70 32, 60 34, 60 42 C60 54, 74 68, 74 68 C74 68, 88 54, 88 42 Z"
      animate={{ rotate: [3, -3, 3] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "74px 50px" }}
    />
    {/* Connecting ribbon */}
    <path d="M37 48 C46 44, 54 52, 63 48" opacity="0.5" />
    {/* Floating dots */}
    <motion.g
      animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    >
      <circle cx="50" cy="20" r="1.6" fill="#4A1A1F" />
      <circle cx="20" cy="74" r="1.3" fill="#4A1A1F" />
      <circle cx="82" cy="78" r="1.3" fill="#4A1A1F" />
    </motion.g>
  </motion.svg>
);

// 2025 — engagement ring with diamond + sparkles ("the proposal")
const RingDoodle = () => (
  <motion.svg
    viewBox="0 0 100 100" fill="none" stroke="#4A1A1F" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"
  >
    {/* Ring band */}
    <motion.ellipse
      cx="50" cy="62" rx="22" ry="20"
      animate={{ rotate: [0, 6, -6, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "50px 62px" }}
    />
    {/* Diamond on top */}
    <motion.g
      animate={{ rotate: [-6, 6, -6] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "50px 32px" }}
    >
      <path d="M40 28 L50 18 L60 28 L50 44 Z" />
      <path d="M40 28 L60 28" />
      <path d="M50 18 L50 44" opacity="0.4" />
      <path d="M40 28 L50 44" opacity="0.4" />
      <path d="M60 28 L50 44" opacity="0.4" />
    </motion.g>
    {/* Twinkles */}
    <motion.g
      animate={{ opacity: [0, 1, 0], scale: [0.6, 1.1, 0.6] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "75px 18px" }}
    >
      <path d="M72 14 L74 17 L77 16 L75 19 L74 22 L73 19 L70 18 L73 16 Z" />
    </motion.g>
    <motion.g
      animate={{ opacity: [1, 0, 1], scale: [1, 0.7, 1] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "22px 30px" }}
    >
      <path d="M19 26 L21 29 L24 28 L22 31 L21 34 L20 31 L17 30 L20 28 Z" />
    </motion.g>
  </motion.svg>
);

// 2026 — wedding bells with bow ("forever begins")
const BellsDoodle = () => (
  <motion.svg
    viewBox="0 0 100 100" fill="none" stroke="#4A1A1F" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"
  >
    {/* Bow ribbon on top */}
    <path d="M44 18 C40 12, 32 14, 36 22 C32 22, 40 28, 44 22 Z" />
    <path d="M56 18 C60 12, 68 14, 64 22 C68 22, 60 28, 56 22 Z" />
    <path d="M48 22 L48 28 M52 22 L52 28" />
    {/* Two bells swinging in opposite phase */}
    <motion.g
      animate={{ rotate: [-10, 10, -10] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "36px 28px" }}
    >
      <path d="M36 30 C26 32, 22 56, 20 70 C28 74, 44 74, 52 70 C50 56, 46 32, 36 30 Z" />
      <path d="M28 70 C32 76, 40 76, 44 70" />
      <circle cx="36" cy="78" r="2" fill="#4A1A1F" />
    </motion.g>
    <motion.g
      animate={{ rotate: [10, -10, 10] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "64px 28px" }}
    >
      <path d="M64 30 C54 32, 50 56, 48 70 C56 74, 72 74, 80 70 C78 56, 74 32, 64 30 Z" opacity="0.85" />
      <path d="M56 70 C60 76, 68 76, 72 70" opacity="0.85" />
      <circle cx="64" cy="78" r="2" fill="#4A1A1F" opacity="0.85" />
    </motion.g>
    {/* Music notes */}
    <motion.g
      animate={{ y: [0, -8, 0], opacity: [0, 1, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    >
      <circle cx="14" cy="44" r="2" fill="#4A1A1F" />
      <path d="M16 44 L16 34" />
    </motion.g>
    <motion.g
      animate={{ y: [0, -8, 0], opacity: [0, 1, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
    >
      <circle cx="88" cy="50" r="2" fill="#4A1A1F" />
      <path d="M90 50 L90 40" />
    </motion.g>
  </motion.svg>
);

// Timeline entry with scroll-driven parallax on the doodle illustration
const TimelineEntry = ({ event, index, isLeft }) => {
  const ref = useRef(null);
  const DoodleCmp = event.Doodle;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const doodleY = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const doodleRotate = useTransform(scrollYProgress, [0, 0.5, 1], [-6, 0, 6]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: "easeOut" }}
      className={`relative mb-12 sm:mb-16 pl-20 ${
        isLeft ? 'sm:pl-0 sm:pr-[52%] sm:text-right' : 'sm:pl-[52%] sm:pr-0 sm:text-left'
      }`}
      data-testid={`story-event-${event.year}`}
    >
      {/* MOBILE: doodle is an absolute circular badge centered on the spine. DESKTOP: doodle is absolute on opposite side. */}
      {/* Mobile doodle — circle on the spine */}
      <motion.div
        style={{ y: doodleY, rotate: doodleRotate }}
        className="sm:hidden absolute left-4 top-1 -translate-x-1/2 w-12 h-12 rounded-full bg-off-white border-2 border-burgundy/40 shadow-sm flex items-center justify-center pointer-events-none z-10"
        aria-hidden="true"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: index * 0.12 + 0.2, type: "spring", stiffness: 140, damping: 14 }}
          className="w-8 h-8 flex items-center justify-center"
        >
          <DoodleCmp />
        </motion.div>
      </motion.div>

      {/* Desktop doodle — absolute on the OPPOSITE side of the spine */}
      <motion.div
        style={{ y: doodleY, rotate: doodleRotate }}
        className={`hidden sm:flex absolute top-0 w-20 h-20 items-center justify-center pointer-events-none ${
          isLeft ? 'left-[calc(50%+0.5rem)]' : 'right-[calc(50%+0.5rem)]'
        }`}
        aria-hidden="true"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: index * 0.12 + 0.2, type: "spring", stiffness: 140, damping: 14 }}
          className="w-full h-full"
        >
          <DoodleCmp />
        </motion.div>
      </motion.div>

      {/* Content */}
      <div className={`sm:px-2 ${isLeft ? 'sm:pr-6' : 'sm:pl-6'}`}>
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: index * 0.12 + 0.15 }}
          className="inline-block bg-burgundy text-white px-3 sm:px-4 py-1 text-xs sm:text-sm font-sans mb-2 sm:mb-3 tracking-wider"
        >
          {event.year}
        </motion.span>
        <h3 className="font-serif text-lg sm:text-2xl text-gray-900 mb-1 italic">{event.title}</h3>
        <p className="text-xs text-burgundy/70 italic mb-2 sm:mb-3">— {event.accent}</p>
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{event.description}</p>
      </div>

      {/* Spine dot — desktop only; on mobile the circular doodle badge sits on the spine */}
      <span
        className="hidden sm:block absolute left-1/2 -translate-x-1/2 top-10 w-2.5 h-2.5 bg-burgundy rounded-full ring-4 ring-off-white"
        aria-hidden="true"
      />
    </motion.div>
  );
};

// Love Story Section
const LoveStorySection = () => {
  const storyEvents = [
    {
      year: "2023",
      title: "The Tap That Changed Everything",
      description: "It began with nothing more than a light tap on Navina's shoulder and that's it, a fleeting gesture that shifted the course of our lives. What started as an ordinary night out with friends dissolved into a conversation that felt anything but ordinary. In the span of a few hours, the noise of the room faded away, leaving only a connection so profound it defied time. We met as strangers, yet by the end of the night, it felt as though our souls had finally found their way home.",
      Doodle: ChampagneToastDoodle,
      accent: "An unexpected hello"
    },
    {
      year: "2024",
      title: "Growing Together",
      description: "What we love most about each other is how easily we do life together. We spent this year exploring different places, trying new cuisines, going to gigs, and making memories in the simplest moments. From spontaneous plans to cooking together, we realised how much we genuinely enjoy building a life side by side.",
      Doodle: HeartsDoodle,
      accent: "Two hearts, one rhythm"
    },
    {
      year: "2025",
      title: "The Proposal",
      description: "On a magical evening in Paris, under the Eiffel Tower, George asked the question that changed our lives.",
      Doodle: RingDoodle,
      accent: "Paris, under the Eiffel Tower"
    },
    {
      year: "2026",
      title: "The Beginning of Forever",
      description: "Surrounded by the people we love most, we are excited to step into our future together. We invite you to join us at voco Manchester City Centre for a celebration of love and new beginnings. Your presence will make our day truly complete as we start a chapter we'll remember forever.",
      Doodle: BellsDoodle,
      accent: "14th November · Manchester"
    }
  ];

  return (
    <section id="story" data-testid="story-section" className="py-16 sm:py-24 bg-off-white">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-burgundy mb-3">
            How It All Started
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 italic">
            Our Love Story
          </motion.h2>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-4">
            <span className="w-12 sm:w-16 h-px bg-burgundy/40" />
            <Heart className="w-4 h-4 text-burgundy" />
            <span className="w-12 sm:w-16 h-px bg-burgundy/40" />
          </motion.div>
        </motion.div>

        {/* Timeline - Mobile Optimized with per-milestone illustrations */}
        <div className="relative">
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            style={{ transformOrigin: "top" }}
            className="absolute left-4 sm:left-1/2 sm:-translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-burgundy via-burgundy/50 to-burgundy"
          />

          {storyEvents.map((event, index) => (
            <TimelineEntry
              key={event.year}
              event={event}
              index={index}
              isLeft={index % 2 === 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Schedule Section - hidden until guest completes RSVP; then shows the appropriate agenda for them
const ScheduleSection = ({ guestInfo, rsvpSubmitted }) => {
  const fullDayEvents = [
    { time: "3:00 PM", title: "Arrival & Pre-Ceremony", description: "Guests arrive at voco Manchester City Centre and are welcomed into the main bar to begin the celebrations.", icon: Clock, ceremonyOnly: true },
    { time: "4:00 PM", title: "Ceremony", description: "Join us as we say \"I do\" in the beautiful ceremony suite.", icon: Heart, ceremonyOnly: true },
    { time: "4:30 PM - 6:00 PM", title: "Post-Ceremony Celebrations", description: "Following the ceremony, grab a drink at the bar and celebrate with family & friends while we take photographs and the reception room is set for the evening celebrations.", icon: Utensils, ceremonyOnly: true },
    { time: "6:15 PM", title: "Reception Entrance", description: "Please join us in welcoming the newlyweds as they make their first appearance to begin the evening's festivities.", icon: Users, ceremonyOnly: true },
    { time: "6:20 PM - 8:00 PM", title: "Speeches, Dinner & Dessert", description: "Join us for a few words from our loved ones, followed by a celebratory buffet dinner and the cutting of our wedding cake.", icon: Utensils, ceremonyOnly: true },
    { time: "8:30 PM", title: "Celebration & Dance", description: "As the live music begins, join us for our first dance. We look forward to a night of celebration, laughter, and dance. Let's make it a night to remember.", icon: Music },
    { time: "12:00 AM", title: "Carriages", description: "As the bar closes and our celebrations draw to a close, we thank you for sharing this special day with us. Safe travels home.", icon: Clock },
  ];

  // Only show the schedule AFTER the guest has submitted their RSVP
  if (!rsvpSubmitted || !guestInfo?.found) {
    return null;
  }

  const showCeremony = guestInfo.ceremony_invited;
  const events = showCeremony ? fullDayEvents : fullDayEvents.filter(e => !e.ceremonyOnly);
  const firstName = guestInfo?.name ? guestInfo.name.split(/\s+/)[0] : '';

  return (
    <section id="schedule" data-testid="schedule-section" className="py-16 sm:py-24 bg-warm-cream">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-burgundy mb-3">
            14th November 2026
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 italic">
            Wedding Day Schedule
          </motion.h2>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 sm:w-16 h-px bg-burgundy/40" />
            <Calendar className="w-4 h-4 text-burgundy" />
            <span className="w-12 sm:w-16 h-px bg-burgundy/40" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="schedule-revealed-notice"
            className="mt-2 inline-flex items-center gap-2 bg-burgundy/10 border border-burgundy/30 px-4 py-3 max-w-xl mx-auto"
          >
            <Sparkles className="w-4 h-4 text-burgundy flex-shrink-0" />
            <p className="text-gray-700 text-xs sm:text-sm text-left">
              Thank you{firstName ? `, ${firstName}` : ''}! Here's your personalised agenda for the day —
              {showCeremony
                ? ' you are invited to share the full day with us.'
                : ' you are warmly invited to join us for the celebration & dance.'}
            </p>
          </motion.div>
        </motion.div>

        <div className="space-y-4 sm:space-y-6">
          {events.map((event, index) => (
            <motion.div
              key={event.time}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`schedule-event-${event.time.replace(/[^0-9]/g, '-')}`}
              className="flex items-start gap-4 sm:gap-6 bg-off-white p-4 sm:p-6 rounded-sm"
            >
              <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-burgundy rounded-full flex items-center justify-center">
                <event.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs sm:text-sm text-burgundy font-medium">{event.time}</span>
                <h3 className="font-serif text-lg sm:text-xl text-gray-900 mt-1 italic">{event.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base mt-1">{event.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// RSVP Section - dual-mode (phone OR first + last name) lookup with optional disambiguation picker
const RSVPSection = ({ onGuestVerified, onRsvpSubmitted }) => {
  const [step, setStep] = useState(1); // 1=lookup, 1.5=pick-match, 2=full form, 3=success, 'already'=returning guest welcome-back
  const [lookupMode, setLookupMode] = useState('phone'); // 'phone' | 'name'
  const [phoneInput, setPhoneInput] = useState('');
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [matches, setMatches] = useState([]);
  const [guest, setGuest] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [formData, setFormData] = useState({
    attendance: '',
    num_guests: '1',
    meal_preference: '',
    dietary_requirements: '',
    song_request: '',
    message: '',
    plus_one_name: '',
    plus_one_meal: '',
    plus_one_dietary: '',
    plus_one_song: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const splitName = (full) => {
    if (!full) return { first: '', last: '' };
    const parts = full.trim().split(/\s+/);
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  const startUpdateExistingRSVP = () => {
    const s = guest?.rsvp_summary || {};
    setFormData({
      attendance: s.attendance || '',
      num_guests: String(s.num_guests || 1),
      meal_preference: s.meal_preference || '',
      dietary_requirements: s.dietary_requirements || '',
      song_request: s.song_request || '',
      message: s.message || '',
      plus_one_name: s.plus_one_name || '',
      plus_one_meal: s.plus_one_meal || '',
      plus_one_dietary: s.plus_one_dietary || '',
      plus_one_song: s.plus_one_song || '',
    });
    setIsUpdating(true);
    setStep(2);
  };

  const proceedToForm = (resolvedGuest) => {
    setGuest(resolvedGuest);
    if (onGuestVerified) {
      onGuestVerified(resolvedGuest);
    }
    if (resolvedGuest?.already_rsvped) {
      // Already submitted — surface schedule + show welcome-back panel instead of the form.
      if (onRsvpSubmitted) onRsvpSubmitted();
      setStep('already');
      return;
    }
    setStep(2);
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    setLookupError('');
    setIsChecking(true);

    try {
      if (lookupMode === 'phone') {
        if (!phoneInput.trim()) {
          setLookupError("Please enter your phone number.");
          setIsChecking(false);
          return;
        }
        const phoneEncoded = encodeURIComponent(phoneInput.trim());
        const res = await axios.get(`${API}/guests/check/${phoneEncoded}`);
        if (!res.data.found) {
          setLookupError("Sorry, your phone number wasn't found on our guest list. Please double-check the country code (e.g. +44, +1) or try looking up by name instead.");
          setIsChecking(false);
          return;
        }
        proceedToForm(res.data);
      } else {
        if (!firstNameInput.trim() || !lastNameInput.trim()) {
          setLookupError("Please enter both your first and last name.");
          setIsChecking(false);
          return;
        }
        const res = await axios.post(`${API}/guests/lookup-by-name`, {
          first_name: firstNameInput.trim(),
          last_name: lastNameInput.trim(),
        });
        const found = res.data.matches || [];
        if (found.length === 0) {
          setLookupError("Sorry, we couldn't find your name on our guest list. Please check the spelling, switch to phone lookup, or contact Navina & George.");
          setIsChecking(false);
          return;
        }
        if (found.length === 1) {
          proceedToForm({ ...found[0], found: true });
          return;
        }
        setMatches(found);
        setStep(1.5);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setLookupError("You've made too many attempts. Please wait a minute and try again.");
      } else {
        setLookupError(detail || "Something went wrong. Please try again or contact Navina & George.");
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handlePickMatch = (m) => {
    setMatches([]);
    proceedToForm({ ...m, found: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { first, last } = splitName(guest?.name);
      const phoneToSend = guest?.phone || phoneInput.trim();
      const payload = {
        first_name: first || guest?.name || 'Guest',
        last_name: last || null,
        phone: phoneToSend,
        attendance: formData.attendance,
        num_guests: parseInt(formData.num_guests, 10),
        meal_preference: formData.meal_preference || null,
        dietary_requirements: formData.dietary_requirements || null,
        song_request: formData.song_request || null,
        message: formData.message || null,
      };

      if (parseInt(formData.num_guests, 10) === 2 && guest?.plus_one_allowed) {
        payload.plus_one = {
          name: formData.plus_one_name.trim(),
          meal_preference: formData.plus_one_meal || null,
          dietary_requirements: formData.plus_one_dietary || null,
          song_request: formData.plus_one_song || null,
        };
      }

      if (isUpdating) {
        await axios.put(`${API}/rsvp/by-phone/${encodeURIComponent(phoneToSend)}`, payload);
        toast.success("Your RSVP has been updated. Thank you!");
      } else {
        await axios.post(`${API}/rsvp`, payload);
        toast.success("Thank you for your RSVP! We can't wait to celebrate with you.");
      }
      if (onRsvpSubmitted) onRsvpSubmitted();
      setStep(3);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && detail.toLowerCase().includes('already')) {
        toast.error("An RSVP with this phone number already exists. Please contact Navina & George if you need to update it.");
      } else if (error.response?.status === 404) {
        toast.error(detail || "Phone number not found on the guest list.");
      } else if (error.response?.status === 400 && typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFlow = () => {
    setStep(1);
    setLookupMode('phone');
    setGuest(null);
    setIsUpdating(false);
    setPhoneInput('');
    setFirstNameInput('');
    setLastNameInput('');
    setMatches([]);
    setLookupError('');
    setFormData({
      attendance: '',
      num_guests: '1',
      meal_preference: '',
      dietary_requirements: '',
      song_request: '',
      message: '',
      plus_one_name: '',
      plus_one_meal: '',
      plus_one_dietary: '',
      plus_one_song: '',
    });
    if (onGuestVerified) onGuestVerified(null);
  };

  const displayFirstName = guest?.name ? splitName(guest.name).first : 'there';

  return (
    <section id="rsvp" data-testid="rsvp-section" className="py-16 sm:py-24 bg-warm-cream">
      <div className="max-w-2xl mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-8 sm:mb-12"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-burgundy mb-3">
            We Would Love You There
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 italic">
            RSVP
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 text-sm sm:text-base px-4">
            Please respond by 15th September 2026. We would be so grateful if you could respond by this date so we can finalise arrangements with the venue.
          </motion.p>
          <motion.p variants={fadeUp} className="text-gray-600 text-sm sm:text-base px-4 mt-3 italic">
            To allow all guests to relax and enjoy the day, we kindly ask that our wedding be an adults-only celebration.
          </motion.p>
        </motion.div>

        {/* Step 1: Lookup by Phone OR Name */}
        {step === 1 && (
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={handleLookup}
            className="bg-off-white p-6 sm:p-8 md:p-10 space-y-5"
            data-testid="rsvp-lookup-form"
          >
            <div className="text-center pb-2">
              {lookupMode === 'phone' ? (
                <Phone className="w-8 h-8 text-burgundy mx-auto mb-3" />
              ) : (
                <UserPlus className="w-8 h-8 text-burgundy mx-auto mb-3" />
              )}
              <h3 className="font-serif text-xl sm:text-2xl text-gray-900 italic">Let's find your invitation</h3>
              <p className="text-gray-600 text-xs sm:text-sm mt-2">
                Look yourself up by either the phone number or with your first &amp; last name.
              </p>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-2 p-1 bg-burgundy/5 border border-burgundy/15 rounded-sm">
              <button
                type="button"
                data-testid="lookup-mode-phone"
                onClick={() => { setLookupMode('phone'); setLookupError(''); }}
                className={`flex-1 py-2.5 px-3 text-sm rounded-sm transition-colors ${lookupMode === 'phone' ? 'bg-burgundy text-white' : 'text-burgundy hover:bg-burgundy/10'}`}
              >
                By Phone
              </button>
              <button
                type="button"
                data-testid="lookup-mode-name"
                onClick={() => { setLookupMode('name'); setLookupError(''); }}
                className={`flex-1 py-2.5 px-3 text-sm rounded-sm transition-colors ${lookupMode === 'name' ? 'bg-burgundy text-white' : 'text-burgundy hover:bg-burgundy/10'}`}
              >
                By Name
              </button>
            </div>

            {lookupMode === 'phone' && (
              <div>
                <Label htmlFor="lookup_phone" className="text-gray-700 mb-2 block text-sm">Phone Number *</Label>
                <Input
                  id="lookup_phone"
                  type="tel"
                  data-testid="rsvp-phone"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 h-12 text-base"
                  placeholder="+44 7123 456789"
                  autoComplete="tel"
                />
                <p className="text-xs text-gray-500 mt-1.5">Include the country code starting with a "+".</p>
              </div>
            )}

            {lookupMode === 'name' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lookup_first" className="text-gray-700 mb-2 block text-sm">First Name *</Label>
                  <Input
                    id="lookup_first"
                    data-testid="rsvp-first-name"
                    value={firstNameInput}
                    onChange={(e) => setFirstNameInput(e.target.value)}
                    className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 h-12 text-base"
                    placeholder="First name"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <Label htmlFor="lookup_last" className="text-gray-700 mb-2 block text-sm">Last Name *</Label>
                  <Input
                    id="lookup_last"
                    data-testid="rsvp-last-name"
                    value={lastNameInput}
                    onChange={(e) => setLastNameInput(e.target.value)}
                    className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 h-12 text-base"
                    placeholder="Last name"
                    autoComplete="family-name"
                  />
                </div>
              </div>
            )}

            {lookupError && (
              <div data-testid="rsvp-lookup-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{lookupError}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="rsvp-lookup-submit"
              disabled={isChecking || (lookupMode === 'phone' ? !phoneInput.trim() : (!firstNameInput.trim() || !lastNameInput.trim()))}
              className="w-full bg-burgundy hover:bg-burgundy-600 text-white py-5 sm:py-6 text-sm sm:text-base tracking-wider"
            >
              {isChecking ? 'Checking…' : 'Continue'}
            </Button>
          </motion.form>
        )}

        {/* Step 1.5: Multiple matches picker */}
        {step === 1.5 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-off-white p-6 sm:p-8 md:p-10 space-y-4"
            data-testid="rsvp-match-picker"
          >
            <div className="text-center pb-2">
              <Users className="w-8 h-8 text-burgundy mx-auto mb-3" />
              <h3 className="font-serif text-xl sm:text-2xl text-gray-900 italic">We found a few matches</h3>
              <p className="text-gray-600 text-xs sm:text-sm mt-2">
                Please pick yourself from the list below to continue.
              </p>
            </div>
            <div className="space-y-2">
              {matches.map((m, i) => (
                <button
                  key={m.id || i}
                  data-testid={`match-option-${i}`}
                  onClick={() => handlePickMatch(m)}
                  className="w-full text-left p-4 bg-white border border-burgundy/20 hover:bg-burgundy/5 hover:border-burgundy/40 transition-colors"
                >
                  <p className="font-medium text-gray-900">{m.name}</p>
                  <p className="text-xs text-gray-500 font-mono mt-1">{m.phone_masked}</p>
                </button>
              ))}
            </div>
            <Button onClick={resetFlow} variant="outline" className="w-full border-burgundy/30 text-burgundy">
              None of these — start over
            </Button>
          </motion.div>
        )}

        {/* Step 2: Full form */}
        {step === 2 && guest && (
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={handleSubmit}
            className="bg-off-white p-6 sm:p-8 md:p-10 space-y-5 sm:space-y-6"
            data-testid="rsvp-form"
          >
            <div className="bg-burgundy/5 border border-burgundy/20 p-4 text-sm text-gray-700">
              <p className="font-medium text-burgundy">Hello {displayFirstName}!</p>
              <p className="mt-1 text-xs sm:text-sm">
                {guest.ceremony_invited
                  ? "You're invited to share the full day with us, starting from 3 PM — ceremony, dinner and dancing."
                  : "You're invited to join us for the evening celebration from 8:30 PM."}
                {guest.plus_one_allowed ? ' You may bring a +1.' : ''}
              </p>
              <button type="button" onClick={resetFlow} className="text-xs text-burgundy underline mt-2" data-testid="rsvp-change-details">
                Not you? Start over
              </button>
            </div>

            <div>
              <Label className="text-gray-700 mb-2 block text-sm">Attendance *</Label>
              <Select
                value={formData.attendance}
                onValueChange={(value) => setFormData({ ...formData, attendance: value })}
                required
              >
                <SelectTrigger data-testid="rsvp-attendance" className="border border-burgundy/20 rounded-sm bg-white focus:ring-0 h-11">
                  <SelectValue placeholder="Please select..." />
                </SelectTrigger>
                <SelectContent className="bg-white border border-burgundy/20 shadow-lg z-[100]">
                  <SelectItem value="accepts" className="hover:bg-burgundy/5">Joyfully Accepts</SelectItem>
                  <SelectItem value="declines" className="hover:bg-burgundy/5">Regretfully Declines</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.attendance === 'accepts' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <Label className="text-gray-700 mb-2 block text-sm">Number of Guests</Label>
                    <Select
                      value={formData.num_guests}
                      onValueChange={(value) => setFormData({ ...formData, num_guests: value })}
                    >
                      <SelectTrigger data-testid="rsvp-guests" className="border border-burgundy/20 rounded-sm bg-white focus:ring-0 h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-burgundy/20 shadow-lg z-[100]">
                        <SelectItem value="1" className="hover:bg-burgundy/5">1 - Just me</SelectItem>
                        {guest.plus_one_allowed && (
                          <SelectItem value="2" className="hover:bg-burgundy/5">2 - Me + my plus one</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {!guest.plus_one_allowed && (
                      <p className="text-xs text-gray-500 mt-1.5 italic">This invitation is for the named guest only.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-2 block text-sm">Your Meal Preference</Label>
                    <Select
                      value={formData.meal_preference}
                      onValueChange={(value) => setFormData({ ...formData, meal_preference: value })}
                    >
                      <SelectTrigger data-testid="rsvp-meal" className="border border-burgundy/20 rounded-sm bg-white focus:ring-0 h-11">
                        <SelectValue placeholder="Please select..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-burgundy/20 shadow-lg z-[100]">
                        <SelectItem value="standard" className="hover:bg-burgundy/5">Standard</SelectItem>
                        <SelectItem value="vegetarian" className="hover:bg-burgundy/5">Vegetarian</SelectItem>
                        <SelectItem value="vegan" className="hover:bg-burgundy/5">Vegan</SelectItem>
                        <SelectItem value="halal" className="hover:bg-burgundy/5">Halal</SelectItem>
                        <SelectItem value="kosher" className="hover:bg-burgundy/5">Kosher</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="dietary" className="text-gray-700 mb-2 block text-sm">Dietary Requirements / Allergies</Label>
                  <Textarea
                    id="dietary"
                    data-testid="rsvp-dietary"
                    value={formData.dietary_requirements}
                    onChange={(e) => setFormData({ ...formData, dietary_requirements: e.target.value })}
                    className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 min-h-[70px] sm:min-h-[80px]"
                    placeholder="Please let us know of any dietary needs or allergies..."
                  />
                </div>

                <div>
                  <Label htmlFor="song" className="text-gray-700 mb-2 block text-sm">Song Request</Label>
                  <Input
                    id="song"
                    data-testid="rsvp-song"
                    value={formData.song_request}
                    onChange={(e) => setFormData({ ...formData, song_request: e.target.value })}
                    className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 h-11"
                    placeholder="What song will get you on the dancefloor?"
                  />
                </div>

                {guest.plus_one_allowed && formData.num_guests === '2' && (
                  <div className="border-t border-burgundy/10 pt-5 space-y-5" data-testid="plus-one-section">
                    <h4 className="font-serif text-lg text-burgundy italic">About your plus one</h4>
                    <div>
                      <Label htmlFor="plus_one_name" className="text-gray-700 mb-2 block text-sm">Plus One Full Name *</Label>
                      <Input
                        id="plus_one_name"
                        data-testid="rsvp-plus-one-name"
                        required
                        value={formData.plus_one_name}
                        onChange={(e) => setFormData({ ...formData, plus_one_name: e.target.value })}
                        className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 h-11"
                        placeholder="Their full name"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Plus One Meal Preference</Label>
                      <Select
                        value={formData.plus_one_meal}
                        onValueChange={(value) => setFormData({ ...formData, plus_one_meal: value })}
                      >
                        <SelectTrigger data-testid="rsvp-plus-one-meal" className="border border-burgundy/20 rounded-sm bg-white focus:ring-0 h-11">
                          <SelectValue placeholder="Please select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-burgundy/20 shadow-lg z-[100]">
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="vegetarian">Vegetarian</SelectItem>
                          <SelectItem value="vegan">Vegan</SelectItem>
                          <SelectItem value="halal">Halal</SelectItem>
                          <SelectItem value="kosher">Kosher</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="plus_one_dietary" className="text-gray-700 mb-2 block text-sm">Plus One Dietary / Allergies</Label>
                      <Textarea
                        id="plus_one_dietary"
                        data-testid="rsvp-plus-one-dietary"
                        value={formData.plus_one_dietary}
                        onChange={(e) => setFormData({ ...formData, plus_one_dietary: e.target.value })}
                        className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 min-h-[60px]"
                        placeholder="Any dietary needs for your plus one..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="plus_one_song" className="text-gray-700 mb-2 block text-sm">Plus One Song Request</Label>
                      <Input
                        id="plus_one_song"
                        data-testid="rsvp-plus-one-song"
                        value={formData.plus_one_song}
                        onChange={(e) => setFormData({ ...formData, plus_one_song: e.target.value })}
                        className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 h-11"
                        placeholder="A song to get them on the dancefloor?"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <div>
              <Label htmlFor="message" className="text-gray-700 mb-2 block text-sm">Message for the Couple (optional)</Label>
              <Textarea
                id="message"
                data-testid="rsvp-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="border border-burgundy/20 rounded-sm bg-white focus:border-burgundy focus-visible:ring-0 min-h-[80px] sm:min-h-[100px]"
                placeholder="Share your wishes or a message for Navina & George..."
              />
            </div>

            <Button
              type="submit"
              data-testid="rsvp-submit"
              disabled={isSubmitting || !formData.attendance}
              className="w-full bg-burgundy hover:bg-burgundy-600 text-white py-5 sm:py-6 text-sm sm:text-base tracking-wider"
            >
              {isSubmitting ? 'Sending...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send RSVP
                </>
              )}
            </Button>
          </motion.form>
        )}

        {/* Step 3: Success */}
        {step === 'already' && guest && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-off-white p-6 sm:p-8 md:p-10"
            data-testid="rsvp-already-submitted"
          >
            <div className="text-center mb-6">
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-burgundy mx-auto mb-3" />
              <p className="text-xs uppercase tracking-[0.2em] text-burgundy/70 mb-2">You're all set</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-burgundy italic mb-2">
                Welcome back, {displayFirstName}!
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                We already have your RSVP on file — thank you. Here's a quick summary for you.
              </p>
            </div>

            <div className="bg-burgundy/5 border border-burgundy/20 p-4 sm:p-5 text-sm space-y-2 mb-6" data-testid="rsvp-already-summary">
              {guest.rsvp_summary?.attendance && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-600">Attending</span>
                  <span className="font-medium text-gray-900 text-right">
                    {guest.rsvp_summary.attendance === 'yes' ? 'Yes, joyfully' :
                     guest.rsvp_summary.attendance === 'no' ? 'Regretfully declines' :
                     guest.rsvp_summary.attendance}
                  </span>
                </div>
              )}
              {guest.rsvp_summary?.num_guests > 1 && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-600">Guests</span>
                  <span className="font-medium text-gray-900">{guest.rsvp_summary.num_guests}</span>
                </div>
              )}
              {guest.rsvp_summary?.plus_one_name && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-600">Plus one</span>
                  <span className="font-medium text-gray-900 text-right">{guest.rsvp_summary.plus_one_name}</span>
                </div>
              )}
              {guest.rsvp_summary?.meal_preference && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-600">Meal</span>
                  <span className="font-medium text-gray-900 text-right capitalize">{guest.rsvp_summary.meal_preference}</span>
                </div>
              )}
              <div className="pt-2 border-t border-burgundy/15 text-xs text-burgundy/80 italic">
                {guest.ceremony_invited
                  ? 'You are invited to share the full day with us — starting at 3 PM.'
                  : 'You are invited to join the evening celebration from 8:30 PM.'}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => document.getElementById('schedule')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="already-view-schedule-btn"
                className="flex-1 bg-burgundy hover:bg-burgundy-600 text-white px-6 py-3"
              >
                <Calendar className="w-4 h-4 mr-2" />
                View Wedding Day Schedule
              </Button>
              <Button
                onClick={startUpdateExistingRSVP}
                variant="outline"
                data-testid="already-update-response-btn"
                className="flex-1 border-burgundy/30 text-burgundy hover:bg-burgundy/5 px-6 py-3"
              >
                Update my response
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-5">
              You can update your response any time before 15th September 2026.
            </p>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-off-white p-8 sm:p-10 text-center"
            data-testid="rsvp-success"
          >
            <Heart className="w-12 h-12 text-burgundy mx-auto mb-4" />
            <h3 className="font-serif text-2xl sm:text-3xl text-burgundy italic mb-3">Thank you, {displayFirstName}!</h3>
            <p className="text-gray-600 text-sm sm:text-base">
              Your RSVP has been received. We can't wait to celebrate with you on 14th November 2026.
            </p>
            <p className="text-gray-600 text-sm sm:text-base mt-3">
              Your personalised wedding day agenda is ready below.
            </p>
            <Button
              onClick={() => document.getElementById('schedule')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="view-agenda-btn"
              className="mt-6 bg-burgundy hover:bg-burgundy-600 text-white px-6 py-3"
            >
              <Calendar className="w-4 h-4 mr-2" />
              View Wedding Day Schedule
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// Registry Section
const RegistrySection = () => {
  return (
    <section id="registry" data-testid="registry-section" className="py-16 sm:py-24 bg-burgundy-600">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-8 sm:mb-12"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3">
            Your Presence is Our Gift
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mb-4 italic">
            Registry
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/80 max-w-xl mx-auto text-sm sm:text-base px-4">
            Your presence at our wedding is the greatest gift we could ask for. However, if you would like to contribute towards our future together, we would be truly grateful for your kindness.
          </motion.p>
          <motion.p variants={fadeUp} className="text-white/70 max-w-xl mx-auto text-sm px-4 mt-3">
            We've made it easy for those who wish to contribute using the options below:
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
        >
          <div className="bg-off-white p-6 sm:p-8 text-center flex flex-col justify-between">
            <div>
              <Gift className="w-10 h-10 sm:w-12 sm:h-12 text-burgundy mx-auto mb-4" />
              <h3 className="font-serif text-xl sm:text-2xl text-gray-900 mb-2 italic">Bank Transfer (Malaysia)</h3>
              <p className="text-gray-600 text-xs sm:text-sm mb-4">For our Malaysian guests, please use the details below:</p>
            </div>
            <div className="bg-burgundy/5 border border-burgundy/15 rounded-sm px-4 py-3 text-left text-sm space-y-1.5" data-testid="registry-bank-my">
              <div className="flex justify-between gap-2"><span className="text-gray-500 text-xs uppercase tracking-wider">Bank</span><span className="font-medium text-gray-900">CIMB</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500 text-xs uppercase tracking-wider">Name</span><span className="font-medium text-gray-900">Navina Selvaraj</span></div>
              <div className="flex justify-between gap-2 items-center"><span className="text-gray-500 text-xs uppercase tracking-wider">Account</span>
                <button
                  type="button"
                  data-testid="registry-bank-copy"
                  onClick={() => { try { navigator.clipboard.writeText('7075521572'); toast.success('Account number copied'); } catch (_) {} }}
                  className="font-mono font-medium text-burgundy hover:underline cursor-pointer"
                  title="Click to copy"
                >7075521572</button>
              </div>
            </div>
          </div>

          <div className="bg-off-white p-6 sm:p-8 text-center flex flex-col items-center justify-center">
            <Gift className="w-10 h-10 sm:w-12 sm:h-12 text-burgundy mx-auto mb-4" />
            <h3 className="font-serif text-xl sm:text-2xl text-gray-900 mb-2 italic">Monzo</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-5 sm:mb-6">Send a gift via Monzo</p>
            <Button
              data-testid="registry-monzo"
              className="bg-burgundy hover:bg-burgundy-600 text-white text-sm"
              onClick={() => window.open('https://monzo.me/navinaselvaraj?h=VLcSmu&d=Wedding%20Contribution%20☺%EF%B8%8F&account_type=personal', '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Contribute via Monzo
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  const nearbyHotels = [
    { name: "voco Manchester City Centre", distance: "On-site venue", rating: "4.5", bookingUrl: "https://www.booking.com/searchresults.html?ss=voco+Manchester+City+Centre&checkin=2026-11-13&checkout=2026-11-15&group_adults=2&no_rooms=1", highlight: true },
    { name: "Kimpton Clocktower Hotel", distance: "8 min walk", rating: "4.6", bookingUrl: "https://www.booking.com/searchresults.html?ss=Kimpton+Clocktower+Hotel+Manchester&checkin=2026-11-13&checkout=2026-11-15&group_adults=2&no_rooms=1" },
    { name: "The Edwardian Manchester", distance: "2 min walk", rating: "4.5", bookingUrl: "https://www.booking.com/searchresults.html?ss=The+Edwardian+Manchester+Radisson&checkin=2026-11-13&checkout=2026-11-15&group_adults=2&no_rooms=1" },
    { name: "DoubleTree by Hilton Manchester", distance: "5 min walk", rating: "4.3", bookingUrl: "https://www.booking.com/searchresults.html?ss=DoubleTree+by+Hilton+Manchester+Piccadilly&checkin=2026-11-13&checkout=2026-11-15&group_adults=2&no_rooms=1" },
    { name: "Travelodge Manchester Piccadilly", distance: "5-7 min walk", rating: "3.8", bookingUrl: "https://www.travelodge.co.uk/hotels/581/Manchester-Piccadilly-hotel?checkIn=13/11/2026&checkOut=15/11/2026&rooms[0][adults]=2&rooms[0][children]=0", budget: true },
    { name: "Premier Inn Manchester City Centre", distance: "10 min walk", rating: "4.0", bookingUrl: "https://www.premierinn.com/gb/en/hotels/england/greater-manchester/manchester/manchester-city-centre-portland-street.html?ARRdd=13&ARRmm=11&ARRyyyy=2026&NIGHTS=2&ROOMS=1&ADULT1=2&CHILD1=0&COT1=0&INTTYP1=DB", budget: true },
    { name: "Browse all Manchester City Centre hotels", distance: "Anywhere central", rating: "—", bookingUrl: "https://www.booking.com/searchresults.html?ss=Manchester+City+Centre&checkin=2026-11-13&checkout=2026-11-15&group_adults=2&no_rooms=1", browse: true },
  ];

  return (
    <section id="location" data-testid="location-section" className="py-16 sm:py-24 bg-off-white">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-burgundy mb-3">
            Join Us At
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 italic">
            Location & Travel
          </motion.h2>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-4">
            <span className="w-12 sm:w-16 h-px bg-burgundy/40" />
            <MapPin className="w-4 h-4 text-burgundy" />
            <span className="w-12 sm:w-16 h-px bg-burgundy/40" />
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-6 sm:mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/ad9dwowa_voco-manchester-9792814427-4x3.jpeg" 
                alt="voco Manchester City Centre" 
                className="w-full h-48 sm:h-64 object-cover mb-4 sm:mb-6"
                style={{ filter: 'grayscale(100%) contrast(1.05)' }}
              />
              <h3 className="font-serif text-xl sm:text-2xl text-gray-900 mb-2 italic">voco Manchester City Centre</h3>
              <p className="text-gray-600 text-sm sm:text-base mb-4">59 Portland St, Manchester M1 3HP</p>
              <Button 
                data-testid="view-map-button"
                className="bg-burgundy hover:bg-burgundy-600 text-white text-sm"
                onClick={() => window.open('https://maps.google.com/?q=voco+Manchester+City+Centre+111+Piccadilly', '_blank')}
              >
                <MapPin className="w-4 h-4 mr-2" />
                View on Map
              </Button>
            </div>

            <div className="bg-warm-cream p-4 sm:p-6">
              <h4 className="font-serif text-lg sm:text-xl text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 italic">
                <Car className="w-5 h-5 text-burgundy" />
                Getting There
              </h4>
              <div className="space-y-2 sm:space-y-3 text-gray-600 text-sm sm:text-base">
                <p><strong>By Train:</strong> Manchester Piccadilly is a 5-minute walk.</p>
                <p><strong>By Car:</strong> Parking at NCP Piccadilly or Q-Park nearby.</p>
                <p><strong>By Taxi:</strong> 20 mins from Manchester Airport.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-serif text-xl sm:text-2xl text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 italic">
              <Hotel className="w-5 h-5 sm:w-6 sm:h-6 text-burgundy" />
              Places to Stay
            </h3>
            <div className="space-y-3">
              {nearbyHotels.map((hotel, index) => (
                <a 
                  key={index} 
                  href={hotel.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  data-testid={`hotel-link-${index}`}
                  className={`p-3 sm:p-4 flex flex-col gap-1 hover:bg-burgundy/5 transition-colors cursor-pointer block ${hotel.highlight ? 'bg-burgundy/10 border-2 border-burgundy/30' : 'bg-warm-cream'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-gray-900 text-sm">{hotel.name}</h4>
                        {hotel.highlight && <span className="text-xs bg-burgundy text-white px-2 py-0.5 rounded">Venue Hotel</span>}
                        {hotel.budget && <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">Budget</span>}
                        {hotel.browse && <span className="text-xs bg-burgundy/70 text-white px-2 py-0.5 rounded">See all</span>}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{hotel.distance}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-burgundy font-medium text-sm">{hotel.rating}</span>
                      <ExternalLink className="w-3 h-3 text-burgundy/60" />
                    </div>
                  </div>
                  {hotel.highlight && <p className="text-xs text-burgundy">Located within the venue - most convenient!</p>}
                </a>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">
              All links pre-filtered for 13–15 November 2026 (the wedding weekend). Book early to secure the best rates.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = ({ guestInfo, rsvpSubmitted }) => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Conditional arrival answer based on the guest's invitation type
  let arrivalAnswer;
  let arrivalLink = null;
  let arrivalLinkLabel = null;
  if (rsvpSubmitted && guestInfo?.found && guestInfo.ceremony_invited) {
    arrivalAnswer = "We kindly ask ceremony guests to arrive from 3:00 PM for welcome drinks before the ceremony begins at 4:00 PM.";
    arrivalLink = "schedule";
    arrivalLinkLabel = "View Wedding Day Schedule";
  } else if (rsvpSubmitted && guestInfo?.found && !guestInfo.ceremony_invited) {
    arrivalAnswer = "You are warmly invited to join us from 8:30 PM for the celebration and dance.";
    arrivalLink = "schedule";
    arrivalLinkLabel = "View Wedding Day Schedule";
  } else {
    arrivalAnswer = "Arrival times depend on your invitation. Submit your RSVP above using your phone number or name, we'll then show your personalised arrival time.";
    arrivalLink = "rsvp";
    arrivalLinkLabel = "Go to RSVP";
  }

  const faqs = [
    { question: "What is the dress code?", answer: "Semi-formal or cocktail attire. Suits encouraged, ties optional." },
    { question: "What time should I arrive?", answer: arrivalAnswer, link: arrivalLink, linkLabel: arrivalLinkLabel },
    { question: "Is the wedding adults-only?", answer: "Yes. To allow all guests to relax and enjoy the day, we kindly ask that our wedding be an adults-only celebration." },
    { question: "Where is the venue?", answer: "voco Manchester City Centre, 59 Portland St, Manchester M1 3HP." },
    { question: "Is there parking available?", answer: "Nearby parking is available at NCP Piccadilly and Q-Park. Manchester Piccadilly Station is just a 5-minute walk from the venue." },
    { question: "Is there accommodation nearby?", answer: "Yes, we have listed recommended hotels in the Travel section, including options across different budgets.", link: "location", linkLabel: "View Travel & Hotels" },
    { question: "Will there be vegetarian/vegan options?", answer: "Yes, please include any dietary requirements when submitting your RSVP." },
    { question: "Can I bring a plus-one?", answer: "Invitations are strictly for named guests only. Where a plus-one has been included, it will be clearly stated on your invitation." },
    { question: "Do you have a gift registry?", answer: "Your presence is the greatest gift we could ask for. However, if you would like to contribute, details can be found in our Registry section.", link: "registry", linkLabel: "View Registry" },
    { question: "How can I contact the couple?", answer: "Email Navina at navchel@gmail.com or George at georgemears95@gmail.com." }
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-16 sm:py-24 bg-warm-cream">
      <div className="max-w-3xl mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-8 sm:mb-12"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-burgundy mb-3">
            Questions?
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 italic">
            FAQ
          </motion.h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-off-white border border-burgundy/10 px-4 sm:px-6"
              >
                <AccordionTrigger 
                  data-testid={`faq-trigger-${index}`}
                  className="text-left font-medium text-gray-900 hover:text-burgundy text-sm sm:text-base py-3 sm:py-4"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-sm sm:text-base pb-3 sm:pb-4">
                  {faq.answer}
                  {faq.link && (
                    <button
                      onClick={() => scrollToSection(faq.link)}
                      data-testid={`faq-link-${faq.link}`}
                      className="text-burgundy underline ml-1 hover:text-burgundy-600"
                    >
                      {faq.linkLabel || 'View'} →
                    </button>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 sm:py-14 bg-burgundy-600 text-center">
      <div className="max-w-4xl mx-auto px-4">
        <p className="font-serif italic text-white text-lg sm:text-xl mb-1">We can't wait to celebrate with you.</p>
        <p className="text-white/80 text-sm sm:text-base mb-5 sm:mb-6">With love,</p>
        <h2 className="font-serif text-2xl sm:text-3xl text-white mb-2 italic">Navina &amp; George</h2>
        <p className="text-white/70 text-sm sm:text-base mb-4 sm:mb-6">14th November 2026</p>
        <div className="flex items-center justify-center gap-2 text-white/60 mb-4 sm:mb-6">
          <span className="w-12 sm:w-16 h-px bg-white/30" />
          <Heart className="w-4 h-4" />
          <span className="w-12 sm:w-16 h-px bg-white/30" />
        </div>
        <p className="text-white/70 text-xs sm:text-sm">
          For any questions, please contact:&nbsp;
          <a href="mailto:navchel@gmail.com" className="underline hover:text-white">navchel@gmail.com</a>
          <span className="px-2 text-white/60">or</span>
          <a href="mailto:georgemears95@gmail.com" className="underline hover:text-white">georgemears95@gmail.com</a>
        </p>
      </div>
    </footer>
  );
};

// Music Toggle - autoplays on load (muted-fallback for strict browsers), user can toggle anytime
const MusicToggle = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio('https://customer-assets.emergentagent.com/job_elegant-union-12/artifacts/u1jhtcye_Dive%20Acoustic.mp3');
    audio.loop = true;
    audio.volume = 0.4;
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    // Sync state with the audio element
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    // Attempt autoplay immediately
    const tryPlay = async () => {
      try {
        await audio.play();
      } catch (_) {
        // Browser blocked autoplay — will start on first user interaction
      }
    };
    tryPlay();

    // Fallback: start playback on first user interaction
    const startOnInteraction = () => {
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(() => {});
      }
    };
    const events = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
    events.forEach(ev => document.addEventListener(ev, startOnInteraction, { passive: true, once: true }));

    return () => {
      events.forEach(ev => document.removeEventListener(ev, startOnInteraction));
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const toggleMusic = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  return (
    <button
      data-testid="music-toggle"
      onClick={toggleMusic}
      className={`music-toggle fixed bottom-20 sm:bottom-20 right-4 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-burgundy text-white flex items-center justify-center shadow-lg hover:bg-burgundy-600 transition-all ${isPlaying ? 'playing' : ''}`}
      aria-label={isPlaying ? 'Pause music' : 'Play music'}
    >
      {isPlaying ? <Pause className="w-5 h-5 sm:w-6 sm:h-6" /> : <Play className="w-5 h-5 sm:w-6 sm:h-6 ml-0.5" />}
    </button>
  );
};

// ============ RSVP Dashboard for Couple (with Guest Management) ============

// --- Guest Management Tab ---
const GuestManagementTab = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', phone: '', plus_one_allowed: false, ceremony_invited: true });
  const [editingId, setEditingId] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [showDelete, setShowDelete] = useState(null);
  const [showCSV, setShowCSV] = useState(false);

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const res = await adminAxios.get(`/guests`);
      setGuests(res.data);
    } catch (_) {
      toast.error("Failed to load guest list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGuests(); }, []);

  const resetForm = () => {
    setForm({ name: '', phone: '', plus_one_allowed: false, ceremony_invited: true });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await adminAxios.put(`/guests/${editingId}`, {
          name: form.name,
          plus_one_allowed: form.plus_one_allowed,
          ceremony_invited: form.ceremony_invited,
        });
        toast.success("Guest updated");
      } else {
        await adminAxios.post(`/guests`, form);
        toast.success("Guest added");
      }
      resetForm();
      fetchGuests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save guest");
    }
  };

  const startEdit = (g) => {
    setEditingId(g.id);
    setForm({
      name: g.name,
      phone: g.phone,
      plus_one_allowed: g.plus_one_allowed,
      ceremony_invited: g.ceremony_invited,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      await adminAxios.delete(`/guests/${id}`);
      toast.success("Guest deleted");
      setShowDelete(null);
      fetchGuests();
    } catch (_) {
      toast.error("Failed to delete");
    }
  };

  const toggleRsvpLinkSent = async (g) => {
    try {
      await adminAxios.put(`/guests/${g.id}`, { rsvp_link_sent: !g.rsvp_link_sent });
      // Optimistic local update so the checkbox flips instantly
      setGuests((prev) => prev.map((x) => x.id === g.id ? { ...x, rsvp_link_sent: !g.rsvp_link_sent } : x));
    } catch (_) {
      toast.error("Failed to update");
    }
  };

  const buildWhatsAppLink = (g) => {
    const phone = (g.phone || '').replace(/[^\d]/g, '');
    const siteUrl = 'https://navinaandgeorge.com';
    const fullName = (g.name || 'there').trim();
    const message =
`Hi ${fullName},

Navina & George would love to celebrate their wedding with you on 14th November 2026 at voco Manchester City Centre.

Please RSVP at ${siteUrl} before 15th September 2026.

When you RSVP, please use your full name "${fullName}" so we can find your invitation.

Can't wait to see you there!`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  const handleCSVImport = async () => {
    const lines = csvText.split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length) {
      toast.error("Paste at least one row.");
      return;
    }
    const parsed = [];
    const errors = [];
    lines.forEach((line, idx) => {
      // Skip header
      if (idx === 0 && /name/i.test(line) && /phone/i.test(line)) return;
      const parts = line.split(',').map(p => p.trim());
      if (parts.length < 2) { errors.push(`Line ${idx + 1}: missing phone`); return; }
      const [name, phone, plusOneStr, ceremonyStr] = parts;
      parsed.push({
        name,
        phone,
        plus_one_allowed: /^(true|yes|y|1)$/i.test(plusOneStr || ''),
        ceremony_invited: ceremonyStr === undefined || ceremonyStr === '' ? true : /^(true|yes|y|1)$/i.test(ceremonyStr),
      });
    });

    if (errors.length) {
      toast.error(errors.slice(0, 3).join('; '));
      return;
    }
    try {
      const res = await adminAxios.post(`/guests/bulk`, parsed);
      toast.success(`Imported: ${res.data.added}, Skipped (duplicates): ${res.data.skipped}`);
      setCsvText('');
      setShowCSV(false);
      fetchGuests();
    } catch (_) {
      toast.error("Bulk import failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* Add/Edit form */}
      <div className="bg-white p-5 sm:p-6 shadow-sm" data-testid="guest-form-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg sm:text-xl text-burgundy italic">
            {editingId ? 'Edit Guest' : 'Add Guest'}
          </h3>
          <Button
            onClick={() => setShowCSV(!showCSV)}
            variant="outline"
            size="sm"
            className="border-burgundy/30 text-burgundy text-xs"
            data-testid="toggle-csv-import"
          >
            {showCSV ? 'Hide bulk import' : 'Bulk import (CSV)'}
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700 text-sm mb-1.5 block">Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="h-10"
                data-testid="guest-name-input"
              />
            </div>
            <div>
              <Label className="text-gray-700 text-sm mb-1.5 block">Phone Number *</Label>
              <Input
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+44 7123 456789"
                className="h-10"
                disabled={!!editingId}
                data-testid="guest-phone-input"
              />
              {editingId && <p className="text-xs text-gray-500 mt-1">Phone cannot be changed. Delete and re-add to update.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-burgundy/5 border border-burgundy/20 p-3 sm:p-4" data-testid="guest-plus-one-toggle-row">
              <p className="text-sm font-medium text-gray-900 mb-1">Plus One Allowed</p>
              <p className="text-xs text-gray-600 mb-3">Can bring a guest</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="guest-plus-one-yes"
                  onClick={() => setForm({ ...form, plus_one_allowed: true })}
                  className={`flex-1 py-2 px-3 text-sm border-2 rounded-sm transition-colors ${form.plus_one_allowed ? 'bg-burgundy text-white border-burgundy' : 'bg-white text-gray-700 border-gray-200 hover:border-burgundy/30'}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="guest-plus-one-no"
                  onClick={() => setForm({ ...form, plus_one_allowed: false })}
                  className={`flex-1 py-2 px-3 text-sm border-2 rounded-sm transition-colors ${!form.plus_one_allowed ? 'bg-burgundy text-white border-burgundy' : 'bg-white text-gray-700 border-gray-200 hover:border-burgundy/30'}`}
                >
                  No
                </button>
              </div>
            </div>
            <div className="bg-burgundy/5 border border-burgundy/20 p-3 sm:p-4" data-testid="guest-ceremony-toggle-row">
              <p className="text-sm font-medium text-gray-900 mb-1">Ceremony Invited</p>
              <p className="text-xs text-gray-600 mb-3">Full day = arrival at 3 PM + ceremony. Evening only = party from 8:30 PM.</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="guest-ceremony-yes"
                  onClick={() => setForm({ ...form, ceremony_invited: true })}
                  className={`flex-1 py-2 px-3 text-sm border-2 rounded-sm transition-colors ${form.ceremony_invited ? 'bg-burgundy text-white border-burgundy' : 'bg-white text-gray-700 border-gray-200 hover:border-burgundy/30'}`}
                >
                  Full day
                </button>
                <button
                  type="button"
                  data-testid="guest-ceremony-no"
                  onClick={() => setForm({ ...form, ceremony_invited: false })}
                  className={`flex-1 py-2 px-3 text-sm border-2 rounded-sm transition-colors ${!form.ceremony_invited ? 'bg-burgundy text-white border-burgundy' : 'bg-white text-gray-700 border-gray-200 hover:border-burgundy/30'}`}
                >
                  Evening only
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="bg-burgundy hover:bg-burgundy-600 text-white" data-testid="guest-save-button">
              {editingId ? <><Save className="w-4 h-4 mr-1" /> Save Changes</> : <><UserPlus className="w-4 h-4 mr-1" /> Add Guest</>}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm} data-testid="guest-cancel-edit">Cancel</Button>
            )}
          </div>
        </form>

        {showCSV && (
          <div className="mt-5 pt-5 border-t border-gray-200" data-testid="csv-import-section">
            <Label className="text-sm text-gray-700 mb-2 block">
              Paste CSV (one guest per row)
            </Label>
            <p className="text-xs text-gray-500 mb-2">
              Format: <span className="font-mono">Name, Phone, PlusOneAllowed (true/false), CeremonyInvited (true/false)</span>
            </p>
            <Textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="min-h-[120px] font-mono text-xs"
              placeholder={"Aunt Priya, +44 7700 900123, false, true\nMike Smith, +1 415 555 0123, true, true\nEvening Guest, +44 7700 900999, false, false"}
              data-testid="csv-textarea"
            />
            <div className="flex gap-2 mt-3">
              <Button onClick={handleCSVImport} className="bg-burgundy hover:bg-burgundy-600 text-white text-sm" data-testid="csv-import-button">
                Import
              </Button>
              <Button variant="outline" onClick={() => { setCsvText(''); setShowCSV(false); }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Guest List */}
      <div className="bg-white p-5 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg sm:text-xl text-burgundy italic">Guest List</h3>
          <span className="text-xs sm:text-sm text-gray-500" data-testid="guest-count">{guests.length} guest{guests.length === 1 ? '' : 's'}</span>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-6 text-sm">Loading…</p>
        ) : guests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserPlus className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No guests yet. Add your first guest above.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {guests.map((g) => (
              <div key={g.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 border border-gray-100 hover:bg-burgundy/5" data-testid={`guest-row-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{g.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{g.phone}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {g.plus_one_allowed ? (
                      <span className="text-xs bg-burgundy/10 text-burgundy px-2 py-0.5 rounded">+1 allowed</span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">No +1</span>
                    )}
                    {g.ceremony_invited ? (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">Full day</span>
                    ) : (
                      <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">Evening only</span>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none ml-1" data-testid={`rsvp-link-sent-label-${g.id}`}>
                      <input
                        type="checkbox"
                        checked={!!g.rsvp_link_sent}
                        onChange={() => toggleRsvpLinkSent(g)}
                        className="w-3.5 h-3.5 accent-burgundy"
                        data-testid={`rsvp-link-sent-${g.id}`}
                      />
                      RSVP link sent
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <a
                    href={buildWhatsAppLink(g)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`whatsapp-${g.id}`}
                    className="inline-flex items-center gap-1 border border-green-600 text-green-700 hover:bg-green-50 text-xs px-2.5 py-1.5 rounded-sm"
                    title="Send pre-filled WhatsApp RSVP message"
                  >
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </a>
                  <Button size="sm" variant="outline" onClick={() => startEdit(g)} className="border-burgundy/30 text-burgundy text-xs" data-testid={`edit-guest-${g.id}`}>
                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowDelete(g.id)} className="border-red-300 text-red-600 text-xs" data-testid={`delete-guest-${g.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDelete(null)}>
          <div className="bg-white max-w-sm w-full p-6 rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl mb-3">Remove this guest?</h3>
            <p className="text-gray-600 text-sm mb-5">This will permanently remove them from your guest list.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDelete(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(showDelete)} data-testid="confirm-delete-guest">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- RSVPs Tab ---
const RSVPsTab = () => {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRsvp, setSelectedRsvp] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const fetchRSVPs = async () => {
    try {
      const response = await adminAxios.get(`/rsvp`);
      setRsvps(response.data);
    } catch (_) {
      toast.error("Failed to load RSVPs");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rsvpId) => {
    try {
      await adminAxios.delete(`/rsvp/${rsvpId}`);
      toast.success("RSVP deleted successfully");
      setShowDeleteConfirm(null);
      fetchRSVPs();
    } catch (_) {
      toast.error("Failed to delete RSVP");
    }
  };

  useEffect(() => { fetchRSVPs(); }, []);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await fetch(`${API}/rsvp/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `navina-george-rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rsvps.length} RSVPs`);
    } catch (_) {
      toast.error("Export failed");
    }
  };

  const acceptedCount = rsvps.filter(r => r.attendance === 'accepts').length;
  const declinedCount = rsvps.filter(r => r.attendance === 'declines').length;
  const totalGuests = rsvps
    .filter(r => r.attendance === 'accepts')
    .reduce((sum, r) => sum + (r.num_guests || 1), 0);

  return (
    <div>
      {/* Stats + export */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div className="bg-white p-4 sm:p-6 text-center shadow-sm">
          <p className="text-3xl sm:text-4xl font-serif text-burgundy">{rsvps.length}</p>
          <p className="text-gray-600 text-sm">Total RSVPs</p>
        </div>
        <div className="bg-white p-4 sm:p-6 text-center shadow-sm">
          <p className="text-3xl sm:text-4xl font-serif text-green-600">{acceptedCount}</p>
          <p className="text-gray-600 text-sm">Accepted</p>
        </div>
        <div className="bg-white p-4 sm:p-6 text-center shadow-sm">
          <p className="text-3xl sm:text-4xl font-serif text-red-600">{declinedCount}</p>
          <p className="text-gray-600 text-sm">Declined</p>
        </div>
        <div className="bg-white p-4 sm:p-6 text-center shadow-sm">
          <p className="text-3xl sm:text-4xl font-serif text-burgundy">{totalGuests}</p>
          <p className="text-gray-600 text-sm">Total Guests</p>
        </div>
      </div>

      <div className="flex justify-end mb-6">
        <Button
          onClick={handleExport}
          disabled={rsvps.length === 0}
          className="bg-burgundy hover:bg-burgundy-600 text-white text-sm"
          data-testid="export-rsvps-csv"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {loading ? (
        <p className="text-center text-gray-600">Loading...</p>
      ) : rsvps.length === 0 ? (
        <div className="bg-white p-8 text-center shadow-sm">
          <p className="text-gray-600">No RSVPs yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rsvps.map((rsvp) => (
            <div key={rsvp.id} className="bg-white p-4 sm:p-6 shadow-sm" data-testid={`rsvp-card-${rsvp.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="font-medium text-lg">{rsvp.first_name} {rsvp.last_name}</h3>
                    {rsvp.attendance === 'accepts' ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm bg-green-50 px-2 py-0.5 rounded">
                        <Check className="w-3 h-3" /> Attending
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600 text-sm bg-red-50 px-2 py-0.5 rounded">
                        <XCircle className="w-3 h-3" /> Declined
                      </span>
                    )}
                    {rsvp.guest_type === 'evening' && (
                      <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">Evening only</span>
                    )}
                    {rsvp.guest_type === 'full' && (
                      <span className="text-xs bg-burgundy/10 text-burgundy px-2 py-0.5 rounded">Full day</span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm font-mono">{rsvp.phone || rsvp.email || '—'}</p>
                  {rsvp.plus_one && (
                    <p className="text-xs text-burgundy mt-1">+1: {rsvp.plus_one.name}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setSelectedRsvp(rsvp)}
                    className="bg-burgundy hover:bg-burgundy-600 text-white text-sm"
                    data-testid={`view-rsvp-${rsvp.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(rsvp.id)}
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-50 text-sm"
                    data-testid={`delete-rsvp-${rsvp.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedRsvp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedRsvp(null)}>
          <div className="bg-white max-w-lg w-full p-6 rounded-sm shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h2 className="font-serif text-2xl text-burgundy italic">{selectedRsvp.first_name} {selectedRsvp.last_name}</h2>
              <button onClick={() => setSelectedRsvp(null)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-5">
              {/* Status badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {selectedRsvp.attendance === 'accepts' ? (
                  <span className="flex items-center gap-1 text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded text-sm font-medium">
                    <Check className="w-4 h-4" /> Joyfully Accepts
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 px-3 py-1 rounded text-sm font-medium">
                    <XCircle className="w-4 h-4" /> Regretfully Declines
                  </span>
                )}
                {selectedRsvp.guest_type === 'evening' ? (
                  <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-1 rounded">Evening only</span>
                ) : (
                  <span className="text-xs bg-burgundy/10 text-burgundy border border-burgundy/20 px-2 py-1 rounded">Full day</span>
                )}
              </div>

              {/* Primary contact row */}
              <div className="bg-burgundy/5 border border-burgundy/15 p-4 rounded-sm">
                <p className="text-[10px] uppercase tracking-wider text-burgundy/70 mb-1">Contact</p>
                <p className="font-mono text-base text-gray-900 break-all">{selectedRsvp.phone || selectedRsvp.email || '—'}</p>
              </div>

              {/* Main guest details */}
              <div className="bg-white border border-gray-100 rounded-sm divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs uppercase tracking-wider text-gray-500">Party size</span>
                  <span className="text-sm font-medium text-gray-900">{selectedRsvp.num_guests}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs uppercase tracking-wider text-gray-500">Meal preference</span>
                  <span className="text-sm font-medium text-gray-900 text-right capitalize">{selectedRsvp.meal_preference || '—'}</span>
                </div>
                {selectedRsvp.dietary_requirements && (
                  <div className="px-4 py-3">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Dietary / allergies</p>
                    <p className="text-sm text-gray-900">{selectedRsvp.dietary_requirements}</p>
                  </div>
                )}
              </div>

              {/* Plus one details */}
              {selectedRsvp.plus_one && (
                <div className="bg-white border border-burgundy/20 rounded-sm">
                  <div className="px-4 py-2 bg-burgundy/5 border-b border-burgundy/20">
                    <p className="text-xs uppercase tracking-wider text-burgundy font-medium">Plus One</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-xs uppercase tracking-wider text-gray-500">Name</span>
                      <span className="text-sm font-medium text-gray-900">{selectedRsvp.plus_one.name}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-xs uppercase tracking-wider text-gray-500">Meal</span>
                      <span className="text-sm font-medium text-gray-900 capitalize">{selectedRsvp.plus_one.meal_preference || '—'}</span>
                    </div>
                    {selectedRsvp.plus_one.dietary_requirements && (
                      <div className="px-4 py-3">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Dietary</p>
                        <p className="text-sm text-gray-900">{selectedRsvp.plus_one.dietary_requirements}</p>
                      </div>
                    )}
                    {selectedRsvp.plus_one.song_request && (
                      <div className="px-4 py-3">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Song request</p>
                        <p className="text-sm text-gray-900">{selectedRsvp.plus_one.song_request}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Optional extras */}
              {selectedRsvp.song_request && (
                <div className="bg-white border border-gray-100 px-4 py-3 rounded-sm">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Song request</p>
                  <p className="text-sm text-gray-900">{selectedRsvp.song_request}</p>
                </div>
              )}

              {selectedRsvp.message && (
                <div className="bg-gray-50 border border-gray-100 px-4 py-3 rounded-sm">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Message for the couple</p>
                  <p className="text-sm italic text-gray-700">"{selectedRsvp.message}"</p>
                </div>
              )}

              <p className="text-xs text-gray-400 text-right">
                Submitted {new Date(selectedRsvp.created_at).toLocaleString()}
              </p>
            </div>

            <Button onClick={() => setSelectedRsvp(null)} className="w-full mt-6 bg-burgundy hover:bg-burgundy-600 text-white">
              Close
            </Button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-white max-w-sm w-full p-6 rounded-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="font-serif text-xl text-gray-900 mb-4">Delete RSVP?</h2>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this RSVP? This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button onClick={() => setShowDeleteConfirm(null)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={() => handleDelete(showDeleteConfirm)} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Attending-only "Send reminder" actions (SMS + WhatsApp via Twilio) ---
const AttendingReminderActions = ({ accepted }) => {
  const [channel, setChannel] = useState('whatsapp');
  const [showTest, setShowTest] = useState(false);
  const [testPhone1, setTestPhone1] = useState('');
  const [testPhone2, setTestPhone2] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const sendRequest = async (body) => {
    setSending(true);
    setLastResult(null);
    try {
      const res = await adminAxios.post('/notifications/send-reminder', body);
      setLastResult(res.data);
      if (res.data.sent === res.data.total) {
        toast.success(`${res.data.sent}/${res.data.total} ${channel.toUpperCase()} sent`);
      } else if (res.data.sent === 0) {
        toast.error(`All ${res.data.total} ${channel.toUpperCase()} failed — see details below`);
      } else {
        toast.warning(`${res.data.sent} sent, ${res.data.failed} failed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // Always point reminders at the production domain — never the preview URL.
  const PRODUCTION_SITE_URL = 'https://navinaandgeorge.com';

  const handleTestSend = async (e) => {
    e.preventDefault();
    const phones = [testPhone1.trim(), testPhone2.trim()].filter(Boolean);
    if (phones.length === 0) { toast.error("Enter at least one phone number"); return; }
    await sendRequest({ channel, test_phones: phones, site_url: PRODUCTION_SITE_URL });
  };

  const handleBulkSend = async () => {
    setConfirmBulk(false);
    if (!accepted.length) { toast.error("No attending guests"); return; }
    await sendRequest({ channel, guest_ids: accepted.map(g => g.id), site_url: PRODUCTION_SITE_URL });
  };

  return (
    <div className="bg-white border border-burgundy/15 shadow-sm p-4 sm:p-5 space-y-4" data-testid="attending-reminder-actions">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h4 className="font-serif text-lg text-burgundy italic mb-1">Send a reminder</h4>
          <p className="text-xs text-gray-600">
            Send a one-tap reminder to everyone attending via Twilio. Pick a channel and either test with 2 numbers first or blast all attending.
          </p>
        </div>

        {/* Channel toggle */}
        <div className="flex gap-1 p-1 bg-burgundy/5 border border-burgundy/15 rounded-sm">
          <button
            type="button"
            onClick={() => setChannel('whatsapp')}
            data-testid="channel-whatsapp"
            className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${channel === 'whatsapp' ? 'bg-burgundy text-white' : 'text-burgundy hover:bg-burgundy/10'}`}
          >WhatsApp</button>
          <button
            type="button"
            onClick={() => setChannel('sms')}
            data-testid="channel-sms"
            className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${channel === 'sms' ? 'bg-burgundy text-white' : 'text-burgundy hover:bg-burgundy/10'}`}
          >SMS</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setShowTest(!showTest)}
          variant="outline"
          size="sm"
          className="border-burgundy/30 text-burgundy text-xs"
          data-testid="toggle-test-send"
        >
          {showTest ? 'Hide test send' : 'Test with 2 numbers'}
        </Button>
        <Button
          onClick={() => setConfirmBulk(true)}
          disabled={sending || accepted.length === 0}
          size="sm"
          className="bg-burgundy hover:bg-burgundy-600 text-white text-xs"
          data-testid="bulk-send-attending"
        >
          Send to all attending ({accepted.length})
        </Button>
      </div>

      {showTest && (
        <form onSubmit={handleTestSend} className="bg-burgundy/5 border border-burgundy/15 p-3 space-y-2" data-testid="test-send-form">
          <p className="text-xs text-gray-700">
            {channel === 'whatsapp'
              ? 'WhatsApp sandbox: each recipient must first send "join <sandbox-code>" to +1 415 523 8886 from their WhatsApp.'
              : 'SMS: requires a configured TWILIO_SMS_FROM in backend/.env.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="tel"
              value={testPhone1}
              onChange={(e) => setTestPhone1(e.target.value)}
              placeholder="+44 7123 456789"
              className="border border-burgundy/20 rounded-sm px-3 py-2 text-sm bg-white"
              data-testid="test-phone-1"
            />
            <input
              type="tel"
              value={testPhone2}
              onChange={(e) => setTestPhone2(e.target.value)}
              placeholder="+60 12 345 6789"
              className="border border-burgundy/20 rounded-sm px-3 py-2 text-sm bg-white"
              data-testid="test-phone-2"
            />
          </div>
          <Button type="submit" disabled={sending} size="sm" className="bg-burgundy hover:bg-burgundy-600 text-white text-xs" data-testid="test-send-submit">
            {sending ? 'Sending…' : `Send test ${channel.toUpperCase()}`}
          </Button>
        </form>
      )}

      {lastResult && (
        <div className="border border-gray-200 rounded-sm text-xs" data-testid="send-result">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex gap-3 flex-wrap">
            <span><strong className="text-green-700">{lastResult.sent}</strong> sent</span>
            <span><strong className="text-red-600">{lastResult.failed}</strong> failed</span>
            <span className="text-gray-500">channel: {lastResult.channel}</span>
          </div>
          <div className="max-h-48 overflow-auto divide-y divide-gray-100">
            {lastResult.results.map((r, i) => (
              <div key={i} className="px-3 py-1.5 flex items-start justify-between gap-3">
                <span className="font-mono text-gray-700">{r.phone}</span>
                {r.status === 'sent' ? (
                  <span className="text-green-700">Sent · {r.twilio_status}</span>
                ) : (
                  <span className="text-red-600 truncate text-right max-w-[60%]" title={r.error}>
                    Failed{r.error_code ? ` (${r.error_code})` : ''}: {r.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmBulk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setConfirmBulk(false)}>
          <div className="bg-white max-w-sm w-full p-6 rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl mb-3">Send reminder to {accepted.length} guest{accepted.length === 1 ? '' : 's'}?</h3>
            <p className="text-gray-600 text-sm mb-5">This will send a {channel.toUpperCase()} message to everyone who has RSVPed yes. Each message counts toward your Twilio balance.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmBulk(false)}>Cancel</Button>
              <Button className="flex-1 bg-burgundy hover:bg-burgundy-600 text-white" onClick={handleBulkSend} data-testid="confirm-bulk-send">Send</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Reminders Tab (pending vs responded) ---
const RemindersTab = () => {
  const [guests, setGuests] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'responded' | 'attending' | 'all'

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [g, r] = await Promise.all([
        adminAxios.get('/guests'),
        adminAxios.get('/rsvp'),
      ]);
      setGuests(g.data);
      setRsvps(r.data);
    } catch (_) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Build lookup by normalised phone
  const rsvpByPhone = {};
  rsvps.forEach(r => {
    if (r.phone) rsvpByPhone[r.phone] = r;
  });

  const enriched = guests.map(g => {
    const r = rsvpByPhone[g.phone];
    return {
      ...g,
      responded: !!r,
      attendance: r?.attendance || null,
      rsvp_date: r?.created_at || null,
    };
  });

  const pending = enriched.filter(g => !g.responded);
  const responded = enriched.filter(g => g.responded);
  const accepted = responded.filter(g => g.attendance === 'accepts');
  const declined = responded.filter(g => g.attendance === 'declines');

  const visible = filter === 'pending' ? pending : filter === 'responded' ? responded : filter === 'attending' ? accepted : enriched;

  const copyReminder = (g) => {
    const first = (g.name || '').split(/\s+/)[0] || 'there';
    const message = `Hi ${first}! Just a gentle reminder to RSVP for Navina & George's wedding on 14th November 2026. You can confirm here: https://navinaandgeorge.com  — N&G 💕`;
    navigator.clipboard?.writeText(message).then(
      () => toast.success(`Reminder for ${first} copied to clipboard`),
      () => toast.error("Copy failed — please copy manually")
    );
  };

  const buildAttendingWhatsApp = (g) => {
    const phone = (g.phone || '').replace(/[^\d]/g, '');
    const first = (g.name || 'there').split(/\s+/)[0];
    const siteUrl = 'https://navinaandgeorge.com';
    const arrivalLine = g.ceremony_invited
      ? `Doors open at 3 PM at voco Manchester City Centre — the ceremony begins at 4 PM.`
      : `We can't wait to see you at voco Manchester City Centre from 8:30 PM for the evening celebration.`;
    const message =
`Hi ${first},

Just a quick reminder — our wedding is on 14th November 2026 and we can't wait to celebrate with you!

${arrivalLine}

The full schedule and venue details are on the site: ${siteUrl}

See you soon,
Navina & George 💛`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 sm:p-5 shadow-sm text-center" data-testid="stat-total">
          <p className="text-3xl sm:text-4xl font-serif text-burgundy">{enriched.length}</p>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">Invited</p>
        </div>
        <div className="bg-white p-4 sm:p-5 shadow-sm text-center" data-testid="stat-responded">
          <p className="text-3xl sm:text-4xl font-serif text-green-600">{responded.length}</p>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">Responded</p>
          <p className="text-[10px] text-gray-400">{accepted.length} yes · {declined.length} no</p>
        </div>
        <div className="bg-white p-4 sm:p-5 shadow-sm text-center" data-testid="stat-pending">
          <p className="text-3xl sm:text-4xl font-serif text-orange-600">{pending.length}</p>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">Pending</p>
        </div>
        <div className="bg-white p-4 sm:p-5 shadow-sm text-center" data-testid="stat-rate">
          <p className="text-3xl sm:text-4xl font-serif text-burgundy">
            {enriched.length ? Math.round((responded.length / enriched.length) * 100) : 0}%
          </p>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">Response rate</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 p-1 bg-burgundy/5 border border-burgundy/15 rounded-sm">
        {[
          { key: 'pending', label: `Pending (${pending.length})` },
          { key: 'responded', label: `Responded (${responded.length})` },
          { key: 'attending', label: `Attending (${accepted.length})` },
          { key: 'all', label: `All (${enriched.length})` },
        ].map(opt => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            data-testid={`filter-${opt.key}`}
            className={`flex-1 py-2 px-3 text-xs sm:text-sm rounded-sm transition-colors ${
              filter === opt.key ? 'bg-burgundy text-white' : 'text-burgundy hover:bg-burgundy/10'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Attending-filter actions: bulk send + test send */}
      {filter === 'attending' && (
        <AttendingReminderActions accepted={accepted} />
      )}

      {/* List */}
      <div className="bg-white shadow-sm">
        {loading ? (
          <p className="p-8 text-center text-gray-500 text-sm">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Heart className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">
              {filter === 'pending'
                ? "Everyone has responded — wonderful!"
                : filter === 'responded'
                ? "No responses yet."
                : filter === 'attending'
                ? "No one has confirmed yes just yet."
                : "Your guest list is empty. Add guests from the Guest List tab."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visible.map(g => (
              <div
                key={g.id}
                data-testid={`reminder-row-${g.id}`}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 hover:bg-burgundy/5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900 text-sm sm:text-base">{g.name}</p>
                    {g.responded ? (
                      g.attendance === 'accepts' ? (
                        <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded">Attending</span>
                      ) : (
                        <span className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded">Declined</span>
                      )
                    ) : (
                      <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded">Pending</span>
                    )}
                    {g.plus_one_allowed && (
                      <span className="text-[10px] bg-burgundy/10 text-burgundy px-2 py-0.5 rounded">+1</span>
                    )}
                    {!g.ceremony_invited && (
                      <span className="text-[10px] bg-orange-50 text-orange-700 px-2 py-0.5 rounded">Evening</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-mono mt-1">{g.phone}</p>
                  {g.responded && g.rsvp_date && (
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Replied {new Date(g.rsvp_date).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {!g.responded && (
                  <Button
                    size="sm"
                    onClick={() => copyReminder(g)}
                    className="bg-burgundy hover:bg-burgundy-600 text-white text-xs"
                    data-testid={`copy-reminder-${g.id}`}
                  >
                    Copy reminder
                  </Button>
                )}

                {g.responded && g.attendance === 'accepts' && (
                  <a
                    href={buildAttendingWhatsApp(g)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`whatsapp-attending-${g.id}`}
                    className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-sm whitespace-nowrap"
                    title="Send a pre-event reminder on WhatsApp"
                  >
                    <MessageCircle className="w-3 h-3" /> WhatsApp reminder
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Dashboard (wraps both tabs with backend-issued JWT auth) ---
const RSVPDashboard = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // On mount: verify any stored token; also listen for token-expired events from interceptor
  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      if (!token) { if (mounted) setIsVerifying(false); return; }
      try {
        await adminAxios.get(`/auth/verify`);
        if (mounted) setIsAuthenticated(true);
      } catch (_) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      } finally {
        if (mounted) setIsVerifying(false);
      }
    };
    verify();

    const onExpire = () => {
      setIsAuthenticated(false);
      toast.error("Session expired. Please sign in again.");
    };
    window.addEventListener("admin-token-expired", onExpire);
    return () => {
      mounted = false;
      window.removeEventListener("admin-token-expired", onExpire);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, res.data.access_token);
      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Incorrect password");
      } else {
        toast.error("Sign-in failed. Please try again.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAuthenticated(false);
    toast.success("Signed out");
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center px-4">
        <div className="text-gray-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 shadow-lg">
          <h1 className="font-serif text-3xl text-burgundy mb-6 italic text-center">Wedding Dashboard</h1>
          <p className="text-gray-600 text-center mb-6">Enter the password to view your guest list & RSVPs</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="border border-burgundy/20 h-11"
              data-testid="dashboard-password"
              autoComplete="current-password"
            />
            <Button
              type="submit"
              className="w-full bg-burgundy hover:bg-burgundy-600 text-white"
              data-testid="dashboard-login"
              disabled={isLoggingIn || !password}
            >
              {isLoggingIn ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <Link to="/" className="block text-center text-burgundy mt-4 text-sm hover:underline">
            ← Back to Wedding Site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white">
      <div className="bg-burgundy text-white py-6 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="font-serif text-2xl sm:text-3xl italic">Wedding Dashboard</h1>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4" /> Site
            </Link>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout"
              className="flex items-center gap-1 text-white/80 hover:text-white text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <Tabs defaultValue="reminders" className="w-full">
          <TabsList className="mb-6 bg-burgundy/10">
            <TabsTrigger value="reminders" data-testid="tab-reminders" className="data-[state=active]:bg-burgundy data-[state=active]:text-white">
              Reminders
            </TabsTrigger>
            <TabsTrigger value="rsvps" data-testid="tab-rsvps" className="data-[state=active]:bg-burgundy data-[state=active]:text-white">
              RSVPs
            </TabsTrigger>
            <TabsTrigger value="guests" data-testid="tab-guests" className="data-[state=active]:bg-burgundy data-[state=active]:text-white">
              Guest List
            </TabsTrigger>
          </TabsList>
          <TabsContent value="reminders">
            <RemindersTab />
          </TabsContent>
          <TabsContent value="rsvps">
            <RSVPsTab />
          </TabsContent>
          <TabsContent value="guests">
            <GuestManagementTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Main Wedding Page
const WeddingPage = () => {
  const [guestInfo, setGuestInfo] = useState(() => {
    try {
      const raw = localStorage.getItem('ng_rsvp_guest');
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });
  const [rsvpSubmitted, setRsvpSubmitted] = useState(() => {
    return localStorage.getItem('ng_rsvp_submitted') === '1';
  });

  const handleGuestVerified = (info) => {
    setGuestInfo(info);
    if (info) {
      try { localStorage.setItem('ng_rsvp_guest', JSON.stringify(info)); } catch (_) {}
    } else {
      localStorage.removeItem('ng_rsvp_guest');
    }
  };

  const handleRsvpSubmitted = () => {
    setRsvpSubmitted(true);
    localStorage.setItem('ng_rsvp_submitted', '1');
  };

  return (
    <>
      <Navigation rsvpSubmitted={rsvpSubmitted} />
      <HeroSection />
      <LoveStorySection />
      <RSVPSection
        onGuestVerified={handleGuestVerified}
        onRsvpSubmitted={handleRsvpSubmitted}
      />
      <ScheduleSection guestInfo={guestInfo} rsvpSubmitted={rsvpSubmitted} />
      <RegistrySection />
      <LocationSection />
      <FAQSection guestInfo={guestInfo} rsvpSubmitted={rsvpSubmitted} />
      <Footer />
      <MusicToggle />
    </>
  );
};

// Main App Component with Routing
function App() {
  return (
    <div className="App bg-off-white">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<WeddingPage />} />
          <Route path="/dashboard" element={<RSVPDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
